import {Icon} from '@wandb/weave/components/Icon';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import _ from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Grid} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';
import useResizeObserver from 'use-resize-observer';

import * as RunSystemMetricsQuery from '../graphql/runSystemMetricsQuery';
import {color} from '../util/colors';
import {trackPageViewed} from '../util/navigation';
import {linesFromSystemMetricsPlot} from '../util/plotHelpers/lines';
import {filterLines} from '../util/plotHelpers/plotHelpers';
import {Line} from '../util/plotHelpers/types';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {Banner} from './Banner';
import {LinePlot} from './vis/LinePlot';

type RunSystemMetricsProps = RunSystemMetricsQuery.QueryResultProps & {
  sweepName?: string;
  group?: string;
};

const LinePlotWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const RunSystemMetrics: React.FC<RunSystemMetricsProps> = React.memo(
  ({runSystemMetricsQuery, sweepName, group}) => {
    // for page view tracking
    useEffect(() => {
      let pageName = 'Run';
      if (group != null) {
        pageName = 'Group run';
      } else if (sweepName != null) {
        pageName = 'Sweep run';
      }
      trackPageViewed(pageName, 'System');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [isDrawing, setIsDrawing] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [metrics, setMetrics] = useState<string[]>([]);
    const elementRef = React.useRef<HTMLDivElement>(null);
    const {width = 1} = useResizeObserver<HTMLDivElement>({
      ref: elementRef,
    });
    const restyleLineByGpuIndex = useCallback((line: Line) => {
      const intMatch = (line.title || '').toString().match(/\d+/);
      if (intMatch) {
        const gpuIndex = intMatch[0];
        line.title = 'GPU ' + gpuIndex;
        line.color = color(parseInt(gpuIndex, 10) % 8);
      }
    }, []);
    const restyleLineByTPUIndex = useCallback((line: Line) => {
      const intMatch = (line.title || '').toString().match(/\d+/);
      if (intMatch) {
        const tpuIndex = intMatch[0];
        line.title = 'TPU ' + tpuIndex;
        line.color = color(parseInt(tpuIndex, 10) % 8);
      }
    }, []);

    const [isHovered, setIsHovered] = React.useState(false);

    const renderPanelIfNotEmpty = useCallback(
      (panel: Panel) => {
        if (panel.lines.length > 0) {
          if (panel.modifyLine) {
            panel.lines.forEach(line => panel.modifyLine!(line));
          }
          return (
            <Grid.Column>
              <div
                className="unpinned-panel"
                ref={elementRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <h5
                  className={classNames('panel-title', {
                    'select-none': isDrawing,
                  })}
                  title={panel.yAxis}>
                  {panel.yAxis}
                </h5>
                <LinePlotWrapper>
                  <LinePlot
                    filteredLines={filterLines(panel.lines)}
                    yAxis={panel.yAxis}
                    xAxis={
                      panel.lines[0] && panel.lines[0].timestep
                        ? _.capitalize(panel.lines[0].timestep)
                        : 'Runtime'
                    }
                    singleRun
                    yDomain={panel.percentage ? [0, 100] : undefined}
                    isHovered={isHovered}
                    isDrawing={isDrawing}
                    setIsDrawing={setIsDrawing}
                  />
                </LinePlotWrapper>
              </div>
            </Grid.Column>
          );
        } else {
          return undefined;
        }
      },
      [isHovered, isDrawing, setIsHovered, setIsDrawing]
    );

    const getLines = useCallback(
      (lineNames: string[]) => {
        if (metrics == null || metrics.length === 0) {
          return [];
        }
        return linesFromSystemMetricsPlot(metrics, lineNames, '_runtime', 0);
      },
      [metrics]
    );

    const getLinesRegex = useCallback(
      (lineNameRegex: RegExp) => {
        if (metrics == null || metrics.length === 0) {
          return [];
        }
        const row0 = metrics[0];
        const keys = _.keys(row0);
        const matches = keys.filter(k => k.match(lineNameRegex));
        return linesFromSystemMetricsPlot(metrics, matches, '_runtime', 0);
      },
      [metrics]
    );

    useEffect(() => {
      // Apollo sometimes returns loading = true, systemMetrics = null. I believe
      // this is when other components that are polling the graphql run field get
      // results. Apollo ends up blowing away the systemMetrics field until the next
      // time our poll succeeds. So we sadly have to cache results here.
      if (!runSystemMetricsQuery.loading && initialLoad) {
        setInitialLoad(false);
      }
      if (
        runSystemMetricsQuery.systemMetrics != null &&
        runSystemMetricsQuery.systemMetrics !== metrics
      ) {
        setMetrics(runSystemMetricsQuery.systemMetrics);
      }
    }, [initialLoad, metrics, runSystemMetricsQuery]);

    if (initialLoad) {
      return <Loader name="run-system-metrics-loader" size="huge" />;
    }
    if (metrics.length === 0) {
      return (
        <p style={{padding: 16}}>
          No System Metrics data saved yet. W&B automatically saves system
          metrics data for runs that run for longer than a few seconds.
        </p>
      );
    }

    const cpuLines: Panel = {
      yAxis: 'CPU Utilization (%)',
      lines: getLines(['system.cpu']),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };
    const memoryLines: Panel = {
      yAxis: 'System Memory Utilization (%)',
      lines: getLines(['system.memory']),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };
    const processMemoryLines: Panel = {
      yAxis: 'Process Memory In Use (non-swap) (MB)',
      lines: getLines(['system.proc.memory.rssMB']),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const processMemoryPercentLines: Panel = {
      yAxis: 'Process Memory In Use (non-swap) (%)',
      lines: getLines(['system.proc.memory.percent']),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };
    const processMemoryAvailableLines: Panel = {
      yAxis: 'Process Memory Available (non-swap) (MB)',
      lines: getLines(['system.proc.memory.availableMB']),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const processCPUThreadsLines: Panel = {
      yAxis: 'Process CPU Threads In Use',
      lines: getLines(['system.proc.cpu.threads']),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const diskLines: Panel = {
      yAxis: 'Disk Utilization (%)',
      lines: getLines(['system.disk$']),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };
    const diskUsagePercentLines: Panel = {
      yAxis: 'Disk Utilization (%)',
      lines: getLinesRegex(/system.disk.(.)+.usagePercent$/),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };
    const diskUsageGBLines: Panel = {
      yAxis: 'Disk Utilization (GB)',
      lines: getLinesRegex(/system.disk.(.)+.usageGB$/),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const diskIOLines: Panel = {
      yAxis: 'Disk I/O Utilization (MB)',
      lines: getLines(['system.disk.in', 'system.disk.out']),
      percentage: false,
      modifyLine: line => {
        line.title = (line.title || '')
          .toString()
          .replace('system.disk.in', 'write')
          .replace('system.disk.out', 'read');
      },
    };
    const networkLines: Panel = {
      yAxis: 'Network Traffic (bytes)',
      lines: getLines(['system.network.recv', 'system.network.sent']),
      percentage: false,
      modifyLine: line => {
        line.title = (line.title || '')
          .toString()
          .replace('system.network.recv', 'received')
          .replace('system.network.sent', 'sent');
      },
    };
    const systemPowerUsageWatts: Panel = {
      yAxis: 'System Power Usage (W)',
      lines: getLinesRegex(/system.system.powerWatts$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuGpuLines: Panel = {
      yAxis: 'GPU Utilization (%)',
      lines: getLinesRegex(/system.gpu.\d+.gpu/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuTempLines: Panel = {
      yAxis: 'GPU Temperature (\u2103)',
      lines: getLinesRegex(/system.gpu.\d+.temp/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuMemoryLines: Panel = {
      yAxis: 'GPU Time Spent Accessing Memory (%)',
      lines: getLinesRegex(/system.gpu.\d+.memory$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuMemoryAllocated: Panel = {
      yAxis: 'GPU Memory Allocated (%)',
      lines: getLinesRegex(/system.gpu.\d+.memory_?[aA]llocated$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuMemoryAllocatedBytes: Panel = {
      yAxis: 'GPU Memory Allocated (bytes)',
      lines: getLinesRegex(/system.gpu.\d+.memory_?[aA]llocated_?[bB]ytes$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuMemoryUsedBytes: Panel = {
      yAxis: 'GPU Memory Used (bytes)',
      lines: getLinesRegex(/system.gpu.\d+.memory_?[uU]sed$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuRecoveryCount: Panel = {
      yAxis: 'GPU Recovery Count',
      lines: getLinesRegex(/system.gpu.\d+.recoveryCount$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuPowerUsage: Panel = {
      yAxis: 'GPU Power Usage (%)',
      lines: getLinesRegex(/system.gpu.\d+.powerPercent$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuEnforcedPowerLimitWatts: Panel = {
      yAxis: 'GPU Enforced Power Limit (W)',
      lines: getLinesRegex(/system.gpu.\d+.enforcedPowerLimitWatts$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuSMClockSpeed: Panel = {
      yAxis: 'GPU SM Clock Speed (MHz)',
      lines: getLinesRegex(/system.gpu.\d+.smClock$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuMemoryClockSpeed: Panel = {
      yAxis: 'GPU Memory Clock Speed (MHz)',
      lines: getLinesRegex(/system.gpu.\d+.memoryClock$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuGraphicsClockSpeed: Panel = {
      yAxis: 'GPU Graphics Clock Speed (MHz)',
      lines: getLinesRegex(/system.gpu.\d+.graphicsClock$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuCorrectedMemoryErrors: Panel = {
      yAxis: 'GPU Corrected Memory Errors',
      lines: getLinesRegex(/system.gpu.\d+.correctedMemoryErrors$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuUncorrectedMemoryErrors: Panel = {
      yAxis: 'GPU Uncorrected Memory Errors',
      lines: getLinesRegex(/system.gpu.\d+.uncorrectedMemoryErrors$/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const gpuEncoderUtilization: Panel = {
      yAxis: 'GPU Encoder Utilization (%)',
      lines: getLinesRegex(/system.gpu.\d+.encoderUtilization$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuGpuLines: Panel = {
      yAxis: 'Process GPU Utilization (%)',
      lines: getLinesRegex(/system\.gpu\.process\.\d+.gpu/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuTempLines: Panel = {
      yAxis: 'Process GPU Temp (\u2103)',
      lines: getLinesRegex(/system\.gpu\.process\.\d+.temp/),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuMemoryLines: Panel = {
      yAxis: 'Process GPU Time Spent Accessing Memory (%)',
      lines: getLinesRegex(/system\.gpu\.process\.\d+.memory$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuMemoryAllocated: Panel = {
      yAxis: 'Process GPU Memory Allocated (%)',
      lines: getLinesRegex(/system\.gpu\.process\.\d+.memory_?[aA]llocated$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuMemoryAllocatedBytes: Panel = {
      yAxis: 'Process GPU Memory Allocated (bytes)',
      lines: getLinesRegex(
        /system\.gpu\.process\.\d+.memory_?[aA]llocated_?[bB]ytes$/
      ),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuPowerUsage: Panel = {
      yAxis: 'Process GPU Power Usage (%)',
      lines: getLinesRegex(/system\.gpu\.process\.\d+.powerPercent$/),
      percentage: true,
      modifyLine: restyleLineByGpuIndex,
    };
    const processGpuEnforcedPowerLimitWatts: Panel = {
      yAxis: 'Process GPU Enforced Power Limit (W)',
      lines: getLinesRegex(
        /system\.gpu\.process\.\d+.enforcedPowerLimitWatts$/
      ),
      percentage: false,
      modifyLine: restyleLineByGpuIndex,
    };
    const tpuDutyCycleLines: Panel = {
      yAxis: 'TPU Duty Cycle (%)',
      lines: getLinesRegex(/system\.tpu\.\d+\.dutyCycle/),
      percentage: true,
      modifyLine: restyleLineByTPUIndex,
    };
    const tpuMemoryUsageLines: Panel = {
      yAxis: 'TPU Memory Usage (%)',
      lines: getLinesRegex(/system\.tpu\.\d+\.memoryUsage/),
      percentage: true,
      modifyLine: restyleLineByTPUIndex,
    };
    const tpuMemoryUsageBytesLines: Panel = {
      yAxis: 'TPU Memory Usage (bytes)',
      lines: getLinesRegex(/system\.tpu\.\d+\.memoryUsageBytes/),
      percentage: false,
      modifyLine: restyleLineByTPUIndex,
    };
    const ipuAverageBoardTemperature: Panel = {
      yAxis: 'IPU Average Board Temperature (\u2103)',
      lines: getLinesRegex(/system\.ipu\.\d+\.average board temp/),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const ipuDieBoardTemperature: Panel = {
      yAxis: 'IPU Die Board Temperature (\u2103)',
      lines: getLinesRegex(/system\.ipu\.\d+\.average die temp/),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const ipuClock: Panel = {
      yAxis: 'IPU Clock (MHz)',
      lines: getLinesRegex(/system\.ipu\.\d+\.clock/),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const ipuPower: Panel = {
      yAxis: 'IPU Power (W)',
      lines: getLinesRegex(/system\.ipu\.\d+\.ipu power/),
      percentage: false,
      modifyLine: line => {
        line.title = '';
      },
    };
    const ipuUtilization: Panel = {
      yAxis: 'IPU Utilization (%)',
      lines: getLinesRegex(/system\.ipu\.\d+\.ipu utilisation \(%\)$/),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };
    const ipuUtilizationSession: Panel = {
      yAxis: 'IPU Utilization (session) (%)',
      lines: getLinesRegex(/system\.ipu\.\d+\.ipu utilisation \(session\)/),
      percentage: true,
      modifyLine: line => {
        line.title = '';
      },
    };

    return (
      <Grid className="panel-grid run-system-metrics" stackable columns={2}>
        {renderPanelIfNotEmpty(cpuLines)}
        {renderPanelIfNotEmpty(memoryLines)}
        {renderPanelIfNotEmpty(processMemoryLines)}
        {renderPanelIfNotEmpty(processMemoryPercentLines)}
        {renderPanelIfNotEmpty(processMemoryAvailableLines)}
        {renderPanelIfNotEmpty(processCPUThreadsLines)}
        {renderPanelIfNotEmpty(diskLines)}
        {renderPanelIfNotEmpty(diskUsagePercentLines)}
        {renderPanelIfNotEmpty(diskUsageGBLines)}
        {renderPanelIfNotEmpty(diskIOLines)}
        {renderPanelIfNotEmpty(networkLines)}
        {renderPanelIfNotEmpty(systemPowerUsageWatts)}
        {renderPanelIfNotEmpty(gpuGpuLines)}
        {renderPanelIfNotEmpty(gpuTempLines)}
        {renderPanelIfNotEmpty(gpuMemoryLines)}
        {renderPanelIfNotEmpty(gpuMemoryAllocated)}
        {renderPanelIfNotEmpty(gpuMemoryAllocatedBytes)}
        {renderPanelIfNotEmpty(gpuMemoryUsedBytes)}
        {renderPanelIfNotEmpty(gpuRecoveryCount)}
        {renderPanelIfNotEmpty(gpuPowerUsage)}
        {renderPanelIfNotEmpty(gpuEnforcedPowerLimitWatts)}
        {renderPanelIfNotEmpty(gpuSMClockSpeed)}
        {renderPanelIfNotEmpty(gpuMemoryClockSpeed)}
        {renderPanelIfNotEmpty(gpuGraphicsClockSpeed)}
        {renderPanelIfNotEmpty(gpuCorrectedMemoryErrors)}
        {renderPanelIfNotEmpty(gpuUncorrectedMemoryErrors)}
        {renderPanelIfNotEmpty(gpuEncoderUtilization)}
        {renderPanelIfNotEmpty(processGpuGpuLines)}
        {renderPanelIfNotEmpty(processGpuTempLines)}
        {renderPanelIfNotEmpty(processGpuMemoryLines)}
        {renderPanelIfNotEmpty(processGpuMemoryAllocated)}
        {renderPanelIfNotEmpty(processGpuMemoryAllocatedBytes)}
        {renderPanelIfNotEmpty(processGpuPowerUsage)}
        {renderPanelIfNotEmpty(processGpuEnforcedPowerLimitWatts)}
        {renderPanelIfNotEmpty(tpuDutyCycleLines)}
        {renderPanelIfNotEmpty(tpuMemoryUsageLines)}
        {renderPanelIfNotEmpty(tpuMemoryUsageBytesLines)}
        {renderPanelIfNotEmpty(ipuAverageBoardTemperature)}
        {renderPanelIfNotEmpty(ipuDieBoardTemperature)}
        {renderPanelIfNotEmpty(ipuClock)}
        {renderPanelIfNotEmpty(ipuPower)}
        {renderPanelIfNotEmpty(ipuUtilization)}
        {renderPanelIfNotEmpty(ipuUtilizationSession)}
      </Grid>
    );
  }
);

const RunSystemMetricsWithQuery =
  RunSystemMetricsQuery.withQuery(RunSystemMetrics);

const RunSystemMetricsWithDeprecationBanner: typeof RunSystemMetricsWithQuery =
  props => {
    return (
      <>
        <TailwindContents>
          <Banner variant="info" className="m-16">
            <div className="flex gap-16">
              <Icon name="lightbulb-info" />
              <div>
                To streamline your experience, we'll soon be removing this tab.
                All system metric information is available in the System section
                of the Workspace tab. Questions? Reach out at{' '}
                <a
                  className="font-semibold hover:text-teal-500"
                  href="mailto:support@wandb.com"
                  rel="noopener noreferrer">
                  support@wandb.com
                </a>
              </div>
            </div>
          </Banner>
        </TailwindContents>
        <RunSystemMetricsWithQuery {...props} />
      </>
    );
  };

export default RunSystemMetricsWithDeprecationBanner;

interface Panel {
  lines: Line[];
  yAxis: string;
  percentage: boolean;
  modifyLine?: (line: Line) => void;
}
